import * as yup from "yup";

export enum SectionType {
  CONTENT = "content",
  MEALS_SUGGESTED = "meals_suggested",
  DAYS_SCHEDULE = "days_schedule",
  SHOPPING_LIST = "shipping_list",
  MEALS_SCHEME = "meals_scheme",
}

enum CollectionType {
  PROGRAM_SECTION_TEMPLATE = "program-section-template",
  VISIT_SCHEDULE = "visit-schedule",
  CLINIC_LOGO = "clinic-logo",
  USER_AVATAR = "user-avatar",
  FOOD_GROUP = "food-group",
  RECIPE = "recipe",
  DIET_MEAL = "diet-meal",
  PRODUCT = "product",
}

export enum LangDto {
  PL = "pl",
  EN = "en",
}

export interface TagDto {
  id: number;
}

export interface IdDto {
  id: number;
}

export interface NutrientDto {
  id: number;
  value: number;
}

export interface SectionCategoryDto {
  id: number;
  description: string;
  descriptionEn: string;
}

export interface SectionBody {
  title: string;
  titleEn: string | null;
  content: string | null;
  contentEn: string | null;
  programSectionCategoryId: number;
  reviewed?: boolean;
  /** Sent on the wire as `description_pl` via getDataAPI's body snake_casing. */
  descriptionPl: string;
  /** Sent on the wire as `description_en` via getDataAPI's body snake_casing. */
  descriptionEn: string | null;
  imageId: number | null;
}

export interface ActionsDto {
  canEdit: boolean;
  canDelete: boolean;
  canVisible: boolean;
}

export interface PhoneDto {
  prefix: string | null;
  number: string | null;
}

export interface DictionaryNameDto {
  lang: "pl" | "en";
  value: string;
}

export interface AddressDto {
  street: string | null;
  streetNumber: string | null;
  houseNumber: string | null;
  city: string | null;
  postCode: string | null;
}

export interface MediaDto {
  id: number;
}

export interface MediaExtendedDto {
  clinicId: number | null;
  collection: CollectionType;
  id: number;
  mineType: string;
  name: string;
  originalName: string;
  path: string;
  size: number;
  url: string;
  s3Url: string;
}

export interface ImageDto {
  url: string;
}
export interface ClinicIdDto {
  id: number | null;
}

export interface TranslationDto {
  langId: LangDto;
  name: string;
}
export interface TranslationLangDto {
  lang: LangDto;
  name: string;
}
export interface TranslationLangTitleDto {
  lang: LangDto;
  title: string;
}

export interface TagCategoryDto {
  id: number;
}

export interface TagExtendedNewDto {
  id: string;
  name: string;
  translations: TranslationDto[];
  clinicN: { id: number } | null;
  tagCategory: { id: number | null };
}

export enum SexDto {
  m = "m",
  w = "w",
}

export interface MeasureDto {
  id: number;
  grams: number;
}

export interface MainProductCategoryDto {
  id: number;
  description: string;
  descriptionPl: string;
  parentId: number;
}

export const tagSchema = yup.object().shape({
  id: yup.number().required(),
});

export const idSchema = yup.object().shape({
  id: yup.number().required(),
});

export const nutrientSchema = yup.object().shape({
  id: yup.number().required(),
  value: yup
    .number()
    .transform((_, value: null | number) => (value === null ? 0 : value))
    .required(),
});

export const sectionSearchCategorySchema = yup.object().shape({
  id: yup.number().required(),
  description: yup.string().required(),
  descriptionEn: yup.string().required(),
});

export const actionsSchema = yup.object().shape({
  canEdit: yup.boolean().required(),
  canDelete: yup.boolean().required(),
  canVisible: yup.boolean().required(),
});

export const phoneSchema = yup.object().shape({
  prefix: yup.string().nullable().default(null),
  number: yup.string().nullable().default(null),
});

export const dictionaryNameSchema = yup.object({
  lang: yup.mixed().oneOf(["pl", "en"]).required(),
  value: yup.string().required(),
});

export const addressSchema = yup.object().shape({
  street: yup.string().nullable().default(null),
  streetNumber: yup.string().nullable().default(null),
  houseNumber: yup.string().nullable().default(null),
  city: yup.string().nullable().default(null),
  postCode: yup.string().nullable().default(null),
});

export const mediaExtendedSchema = yup.object().shape({
  clinicId: yup.number().nullable().default(null),
  collection: yup
    .mixed<CollectionType>()
    .oneOf(Object.values(CollectionType))
    .required(),
  id: yup.number().required(),
  mineType: yup.string().required(),
  name: yup.string().required(),
  originalName: yup.string().required(),
  path: yup.string().required(),
  size: yup.number().required(),
  url: yup.string().required(),
  s3Url: yup.string().required(),
});

export const imageSchema = yup.object().shape({
  url: yup.string().required(),
});

export const clinicIdSchema = yup.object().shape({
  id: yup.number().nullable().default(null),
});

export const translationSchema = yup.object().shape({
  langId: yup.mixed<LangDto>().oneOf(Object.values(LangDto)).required(),
  name: yup.string().ensure(),
});

export const translationLangSchema = yup.object({
  lang: yup.mixed<LangDto>().oneOf(Object.values(LangDto)).required(),
  name: yup.string().ensure(),
});

export const tagCategorySchema = yup.object().shape({
  id: yup.number().required(),
});

export const translationLangTitleSchema = yup.object({
  lang: yup.mixed<LangDto>().oneOf(Object.values(LangDto)).required(),
  title: yup.string().ensure(),
});

const tagCategoryNullableSchema = yup.object().shape({
  id: yup.number().nullable().default(null),
});

export const tagExtendedNewSchema = yup.object().shape({
  id: yup.string().required(),
  name: yup.string().required(),
  translations: yup.array().of(translationSchema).required(),
  clinicN: idSchema.nullable().default(null),
  tagCategory: tagCategoryNullableSchema.required(),
});

export const sexSchema = yup.mixed<SexDto>().oneOf(Object.values(SexDto));

export const measureSchema = yup.object().shape({
  id: yup.number().required(),
  grams: yup.number().required(),
});

export const mainProductCategorySchema = yup.object().shape({
  id: yup.number().required(),
  descriptionPl: yup.string().required(),
  description: yup.string().ensure(),
  parentId: yup.number().required(),
});

// Shared by the diet / recipe / food nutrient endpoints, which return the
// same nutrient array shape.
export const getNutrientsResponseSchema = yup.array().of(
  yup.object().shape({
    units: yup.string().nullable(),
    value: yup.number().required(),
    id: yup.number().required(),
    nutrientCategoryId: yup.number().required(),
    descriptionEn: yup.string().required(),
    descriptionPl: yup.string().required(),
    dietitianVisible: yup.boolean().required(),
    patientVisible: yup.boolean().required(),
    diaryVisible: yup.boolean().required(),
  }),
);

export interface GetNutrientsResponse {
  id: number;
  nutrientCategoryId: number;
  descriptionEn: string;
  descriptionPl: string;
  dietitianVisible: boolean;
  patientVisible: boolean;
  diaryVisible: boolean;
  units?: string;
  value: number;
}

// Any uploaded media (visit attachments, client avatars) — not a visit concept.
export interface FileData {
  id: number;
  clinicId: number;
  collection: string;
  name: string;
  originalName: string;
  mineType: string;
  size: number;
  path: string;
  url: string;
}

export const fileDataSchema = yup.object().shape({
  id: yup.number().required(),
  clinicId: yup.number().required(),
  collection: yup.string().required(),
  name: yup.string().required(),
  originalName: yup.string().required(),
  mineType: yup.string().required(),
  size: yup.number().required(),
  path: yup.string().required(),
  url: yup.string().required(),
});
